/**
 * Format price as currency
 * @param {number} price - Price to format
 * @returns {string} Formatted price string
 */
export function formatPrice(price) {
  if (price == null || isNaN(price)) return 'N/A';
  
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
}

/**
 * Format number with commas
 * @param {number} num - Number to format
 * @returns {string} Formatted number string
 */
export function formatNumber(num) {
  if (num == null || isNaN(num)) return 'N/A';
  return new Intl.NumberFormat('en-CA').format(num);
}

/**
 * Calculate confidence range percentage (half-width for ± display)
 * @param {number} lower - Lower bound
 * @param {number} upper - Upper bound
 * @param {number} predicted - Predicted price
 * @returns {number} Confidence range as percentage (half-width, suitable for ± display)
 */
export function calculateConfidenceRange(lower, upper, predicted) {
  if (!lower || !upper || !predicted) return 0;
  const range = upper - lower;
  // Return half the total width so ±X% is accurate (X on each side)
  return (range / predicted) * 100 / 2;
}

/**
 * Get condition display information (converts 1-5 scale to 1-10 with label and color)
 * @param {number} condition - Condition rating (1-5 scale)
 * @returns {Object|null} Object with score (1-10), label, and color class, or null if invalid
 */
export function getConditionDisplay(condition) {
  if (!condition || condition <= 0) return null;
  
  const score = (condition * 2).toFixed(1); // Convert 1-5 to 1-10
  let label = 'Poor';
  let color = 'text-red-600 dark:text-red-400';
  
  if (condition >= 4.5) {
    label = 'Excellent';
    color = 'text-emerald-600 dark:text-emerald-400';
  } else if (condition >= 3.5) {
    label = 'Good';
    color = 'text-green-600 dark:text-green-400';
  } else if (condition >= 2.5) {
    label = 'Average';
    color = 'text-yellow-600 dark:text-yellow-400';
  } else if (condition >= 1.5) {
    label = 'Fair';
    color = 'text-orange-600 dark:text-orange-400';
  }
  
  return { score, label, color };
}

/**
 * Returns the appropriate geographic label for a neighbourhood/area name.
 * Handles wards, combined/merged zones, and standard neighbourhoods.
 *
 * @param {string|null} name - The raw neighbourhood name from the API
 * @returns {string} - "District", "Market Zone", or "Neighbourhood"
 */
export function getAreaLabel(name) {
  if (!name) return 'Neighbourhood'
  const n = name.trim()
  if (/\bward\b/i.test(n)) return 'District'
  if (/ [/&+] /.test(n) || /\bcombined\b/i.test(n)) return 'Market Zone'
  return 'Neighbourhood'
}

/**
 * Normalize neighbourhood display names for UI:
 * - Converts snake_case to spaced words
 * - Title-cases only when the source is all lowercase
 * - Leaves already-capitalized names untouched
 *
 * @param {string|null|undefined} name
 * @returns {string}
 */
// Strip trailing GeoJSON numeric codes e.g. "Forest Hill North (102)" → "Forest Hill North"
export function cleanNeighbourhoodName(name) {
  if (!name) return ''
  return String(name).replace(/\s*\(\d+\)\s*$/, '').trim()
}

export function formatNeighborhoodName(name) {
  if (!name) return '—'
  const cleaned = cleanNeighbourhoodName(name)
  const spaced = cleaned.replace(/_/g, ' ').replace(/\s+/g, ' ').trim()
  if (!spaced) return '—'
  if (spaced !== spaced.toLowerCase()) return spaced
  return spaced
    .split(' ')
    .map(part => (part ? part[0].toUpperCase() + part.slice(1) : part))
    .join(' ')
}
